import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'
import { apiClient } from '@/lib/api'
import {
  getAccessToken, msUntilExpiry, refreshAccessToken, clearTokens,
  getImpersonationInfo, startImpersonation as storeImpersonationTokens, exitImpersonation as restoreAdminTokens,
  IMPERSONATION_ENDED_EVENT,
  type ImpersonationInfo,
} from '@/lib/tokenRefresh'

interface User {
  id: string
  email: string
  first_name?: string
  last_name?: string
  full_name?: string
  role: string
  subscription_tier?: string
  request_limit?: number
  requests_used?: number
  permissions: string[]
  modules?: string[]
  admin_approved?: boolean
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string, turnstileToken?: string, rememberMe?: boolean) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
  isLoading: boolean
  isAuthenticated: boolean
  impersonation: ImpersonationInfo | null
  startImpersonation: (accessToken: string, expiresIn: number, targetEmail: string, targetUserId: string) => Promise<void>
  exitImpersonation: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [impersonation, setImpersonation] = useState<ImpersonationInfo | null>(() => getImpersonationInfo())
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const impersonationTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Proactively renew the access token a couple minutes before it expires,
  // instead of only reacting after a request 401s. As long as this tab stays
  // open, a valid session should never actually hit that expiry — the
  // reactive 401-then-refresh path in lib/api.ts is just the safety net for
  // when this timer didn't get a chance to fire (tab was asleep, etc).
  function scheduleProactiveRefresh(accessToken: string) {
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    const remaining = msUntilExpiry(accessToken)
    if (remaining === null) return
    const delay = Math.max(remaining - 2 * 60_000, 5_000) // 2 min early, floor at 5s
    refreshTimer.current = setTimeout(async () => {
      const newToken = await refreshAccessToken()
      if (newToken) scheduleProactiveRefresh(newToken)
    }, delay)
  }

  useEffect(() => {
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current)
      if (impersonationTimer.current) clearTimeout(impersonationTimer.current)
    }
  }, [])

  // If the page was reloaded mid-impersonation, re-arm the auto-exit timer
  // for whatever time is left — the impersonation token itself doesn't
  // survive a real logout, but a reload keeps localStorage intact.
  useEffect(() => {
    if (!impersonation) return
    const remaining = impersonation.expiresAt - Date.now()
    if (remaining <= 0) {
      void exitImpersonationHandler()
      return
    }
    impersonationTimer.current = setTimeout(() => {
      void exitImpersonationHandler()
    }, remaining)
    return () => {
      if (impersonationTimer.current) clearTimeout(impersonationTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonation?.expiresAt])

  // Fired by lib/api.ts / lib/apiRequest.ts when a 401 survives a
  // silent-refresh attempt while impersonating — see IMPERSONATION_ENDED_EVENT's
  // doc comment in tokenRefresh.ts. Falls back to the admin's own session
  // instead of the fetch wrappers' usual forceLogout().
  useEffect(() => {
    const handler = () => { void exitImpersonationHandler() }
    window.addEventListener(IMPERSONATION_ENDED_EVENT, handler)
    return () => window.removeEventListener(IMPERSONATION_ENDED_EVENT, handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Check if user is already logged in
    const token = getAccessToken()
    if (token) {
      scheduleProactiveRefresh(token)
      // Set token in API client
      apiClient.setToken(token)
      
      // Validate token and fetch actual user data
      apiClient.getCurrentUser()
        .then((userData: any) => {
          setUser({
            id: userData.id || userData.user_id,
            email: userData.email,
            first_name: userData.first_name,
            last_name: userData.last_name,
            full_name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email,
            role: userData.role || 'user',
            subscription_tier: userData.subscription_tier,
            request_limit: userData.request_limit,
            requests_used: userData.requests_used || userData.request_count,
            permissions: userData.role === 'admin' ? ['read', 'write', 'admin', 'export'] : ['read', 'write', 'export'],
            modules: userData.modules || [],
            admin_approved: userData.admin_approved !== false
          })
        })
        .catch((error) => {
          console.error('Token validation failed:', error)
          // Token invalid, clear it
          apiClient.clearToken()
          setUser(null)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setIsLoading(false)
    }
  }, [])

  const login = async (email: string, password: string, turnstileToken?: string, rememberMe = false) => {
    try {
      // Use real login endpoint
      const response = await apiClient.login(email, password, turnstileToken, rememberMe)
      scheduleProactiveRefresh(response.access_token)

      // Fetch actual user data after login
      const userData: any = await apiClient.getCurrentUser()
      
      // Set user data from API response
      setUser({
        id: userData.id || userData.user_id,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        full_name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email,
        role: userData.role || 'user',
        subscription_tier: userData.subscription_tier,
        request_limit: userData.request_limit,
        requests_used: userData.requests_used || userData.request_count,
        permissions: userData.role === 'admin' ? ['read', 'write', 'admin', 'export'] : ['read', 'write', 'export'],
        modules: userData.modules || [],
        admin_approved: userData.admin_approved !== false
      })
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const logout = () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    clearTokens()
    apiClient.clearToken()
    setUser(null)
  }

  const refreshUser = async () => {
    try {
      const userData: any = await apiClient.getCurrentUser()
      setUser({
        id: userData.id || userData.user_id,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        full_name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email,
        role: userData.role || 'user',
        subscription_tier: userData.subscription_tier,
        request_limit: userData.request_limit,
        requests_used: userData.requests_used || userData.request_count,
        permissions: userData.role === 'admin' ? ['read', 'write', 'admin', 'export'] : ['read', 'write', 'export'],
        modules: userData.modules || [],
        admin_approved: userData.admin_approved !== false
      })
    } catch (error) {
      console.error('Failed to refresh user data:', error)
    }
  }

  // Admin viewing another user's session (POST /admin/users/{id}/impersonate).
  // See lib/tokenRefresh.ts for the token-swap/backup mechanics.
  const startImpersonationHandler = async (accessToken: string, expiresIn: number, targetEmail: string, targetUserId: string) => {
    storeImpersonationTokens(accessToken, expiresIn, {
      targetEmail,
      targetUserId,
      adminEmail: user?.email || '',
    })
    setImpersonation(getImpersonationInfo())
    if (refreshTimer.current) clearTimeout(refreshTimer.current) // impersonation token isn't refreshable
    await refreshUser()
  }

  const exitImpersonationHandler = async () => {
    // Idempotency guard: the fetch wrappers can dispatch IMPERSONATION_ENDED_EVENT
    // from multiple concurrent 401s (several components mid-flight at once),
    // and the auto-expiry timer below could in principle race with either. A
    // second call must no-op, not fall into the "no backup found" branch and
    // log the admin all the way out right after a successful restore.
    if (!getImpersonationInfo()) return
    const restored = restoreAdminTokens()
    if (impersonationTimer.current) clearTimeout(impersonationTimer.current)
    setImpersonation(null)
    if (restored) {
      const token = getAccessToken()
      if (token) scheduleProactiveRefresh(token)
      await refreshUser()
    } else {
      // No admin session to restore (backup missing/already consumed) — safest
      // fallback is a real logout rather than leaving a half-swapped session.
      logout()
    }
  }

  const value = {
    user,
    login,
    logout,
    refreshUser,
    isLoading,
    isAuthenticated: !!user,
    impersonation,
    startImpersonation: startImpersonationHandler,
    exitImpersonation: exitImpersonationHandler,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}